/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useCallback, useEffect, useState } from "react";
import AccountsForm from "components/forms/AccountingForm";
import Button from "components/generals/Button";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import { CoreAPI_URLs } from "constants/http.const";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import useHttp from "hooks/http.hook";
import { TableProps } from "interfaces";
import { useTranslation } from "react-i18next";
import { AccountingInterface, AccountingMovementInterface, QueryResultInterface, HeaderEnum } from "@malmur-technologies/medicalsystem-shared";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { getNewAccountingDataContent } from "utils/model.utils";
import { useNavigate } from "react-router-dom";
import { calculateAccountingTotals } from "utils/accountingMovements.utils";

/**
 * Paged response structure for paginated API results.
 */
interface PagedResponse<T> {
  total: number;
  page: number;
  hasMorePages: boolean;
  results: T[];
}

/**
 * Properties for the AccountingTable component.
 */
interface AccountingTableProps extends TableProps<AccountingInterface> {
  isEmbedded?: boolean;
}

/**
 * Accounting table component for displaying and managing patient accounting records.
 * 
 * Features:
 * - Displays accounting information in sortable data table
 * - Shows patient name, SSN, and accounting details
 * - View accounting movements (transactions) for each account
 * - Navigate to detailed accounting movements page
 * - Edit accounting record via modal with AccountsForm
 * - Automatic data loading on mount (unless embedded)
 * - Embedded mode for use within other components
 * - Sorted patient list alphabetically by full name
 * - Safe handling of missing or null patient data
 * 
 * Table Columns:
 * - Patient name (full name from person record)
 * - SSN (Social Security Number)
 * - Additional accounting details (balance, status, etc.)
 * - Action buttons (view movements, edit)
 * 
 * Data Loading:
 * - Standalone mode: Auto-loads all accounting records on mount
 * - Embedded mode: Uses provided modelList prop (parent manages data)
 * 
 * Navigation:
 * - "View Movements" button navigates to /accountingMovements
 * - Passes accountingId and patient info via state
 * - Used for detailed transaction history view
 * 
 * Security:
 * - Permission-based access control via PermissionWrapper
 * - Validates entity permissions before operations
 * - Uses secure API endpoints for data fetching
 * 
 * @param {AccountingTableProps} props - Component properties
 * @param {AccountingInterface[]} props.modelList - Array of accounting records
 * @param {Function} props.setModelList - Callback to update accounting list
 * @param {boolean} [props.isEmbedded] - Whether table is embedded in another component
 * 
 * @returns {JSX.Element} Accounting data table with movement viewing
 * @param {AccountingInterface[]} props.modelList - Array of accounting records to display
 * @param {Function} props.setModelList - Callback to update accounting list
 * @param {boolean} props.isEmbedded - Flag indicating embedded mode (no auto-load)
 * 
 * @returns {JSX.Element} Accounting data table with movements navigation
 */
const AccountingTable: React.FC<AccountingTableProps> = ({
  modelList,
  setModelList,
  isEmbedded = false,
  ...props
}) => {
  const [selectedAccount, setSelectedAccount] = useState<AccountingInterface>(
    getNewAccountingDataContent()
  );
  const [balancesByAccountingId, setBalancesByAccountingId] = useState<Record<string, number>>({}
  );
  const [activeEditAccountModal, setActiveEditAccountingModal] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<FormActionType>();
  const { t } = useTranslation();
  const [isLoading, _error, sendRequest] = useHttp();
  const navigate = useNavigate();

  // Load balances for a list of accountings
  const loadBalancesForAccountings = useCallback(
    async (accountings: AccountingInterface[]) => {
      const newBalances: Record<string, number> = {};

      // Prepare parallel requests (each resolves to { accountingId, response } even on error)
      const requests = accountings
        .filter((a) => a?.id)
        .map((accounting) =>
          sendRequest<QueryResultInterface<AccountingMovementInterface>>(
            `${CoreAPI_URLs.ACCOUNTING_MOVEMENT_GENERAL_OPERATIONS}/all`,
            (res) => res,
            "GET",
            undefined,
            [{ header: HeaderEnum.PATIENT_ACCOUNT_NUMBER, value: accounting.id }]
          )
            .then((response) => ({ accountingId: accounting.id!, response }))
            .catch(() => ({ accountingId: accounting.id!, response: null }))
        );

      try {
        const results = await Promise.all(requests);
        for (const { accountingId, response } of results) {
          const movements = response?.data?.results || [];
          if (movements.length > 0) {
            const totals = calculateAccountingTotals(movements);
            newBalances[accountingId] = totals.balancePatient;
          } else {
            newBalances[accountingId] = 0;
          }
        }
      } catch (e) {
        console.error("Error loading balances in parallel:", e);
      }

      setBalancesByAccountingId((prev) => ({ ...prev, ...newBalances }));
    },
    [sendRequest]
  );

  useEffect(() => {
    if (!isEmbedded) {
      sendRequest<PagedResponse<AccountingInterface>, void>(
        CoreAPI_URLs.ACCOUNTING_GET_ALL,
        (result) => {
          setModelList(result.results);
          // Load balances after getting accountings
          loadBalancesForAccountings(result.results);
        },
        "GET"
      );
    }
  }, [isEmbedded, sendRequest, loadBalancesForAccountings, setModelList]);

  // Load balances when modelList changes (for embedded mode)
  useEffect(() => {
    if (!isEmbedded) return;
    if (modelList.length === 0) {
      setBalancesByAccountingId({});
      return;
    }

    loadBalancesForAccountings(modelList);
  }, [modelList, isEmbedded, loadBalancesForAccountings]);
  
  const openMovementsHandler = (accounting: AccountingInterface) => {
    navigate(`/accountingMovements`, {
      state: { accountingId: accounting.id, patient: accounting.patient },
    });
  };

  const getUITable = (list: AccountingInterface[]) => {
    const safeList = Array.isArray(list) ? list : [];
    const sortedList = props.apiPagination
      ? safeList
      : [...safeList].sort((a, b) => {
          const nameA =
            `${a.patient?.person?.name ?? ""} ${a.patient?.person?.lastname ?? ""}`.toLowerCase();
          const nameB =
            `${b.patient?.person?.name ?? ""} ${b.patient?.person?.lastname ?? ""}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });

    return {
      columns: [
        {
          Header: t("accountingsTable.patient"),
          accessor: "patient.person.name",
          Cell: ({ row }: { row: any }) => {
            const accounting = row.original;
            const person = accounting?.patient?.person;

            if (!person) {
              return <DefaultCell value={t("accountingsTable.noPatient")} />;
            }

            const fullName = `${person.name} ${person.lastname}`;
            return <DefaultCell value={fullName} />;
          },
        },
        {
          Header: t("accountingsTable.ssn"),
          accessor: "patient.ssn",
          Cell: ({ row }: { row: any }) => {
            const accounting = row.original;
            const ssn = accounting?.patient?.ssn;

            if (!ssn) {
              return <DefaultCell value={t("accountingsTable.noSSN")} />;
            }
            return <DefaultCell value={ssn} />;
          },
        },
        {
          Header: t("accountingsTable.address"),
          accessor: "patient.person",
          disableSortBy: true,
          Cell: ({ row }: { row: any }) => {
            const accounting = row.original;
            const address = accounting?.patient?.person?.address;

            if (!address) {
              return <DefaultCell value={t("accountingsTable.noAddress")} />;
            }
            return <DefaultCell value={address} />;
          },
        },
        {
          Header: t("accountingsTable.startingDate"),
          accessor: "startingDate",
          Cell: ({ value }: { value: any }) => {
            return <DefaultCell value={value || t("accountingsTable.noDate")} />;
          },
        },
        {
          Header: t("accountingsTable.closingDate"),
          accessor: "closingDate",
          Cell: ({ value }: { value: any }) => {
            return <DefaultCell value={value || t("accountingsTable.noDate")} />;
          },
        },
        {
          Header: t("accountingsTable.status"),
          accessor: "id",
          id: "debtStatus",
          disableSortBy: true,
          Cell: ({ row }: { row: any }) => {
            const accounting: AccountingInterface = row.original;
            const balance = accounting.id ? balancesByAccountingId[accounting.id] : undefined;
            
            if (balance === undefined) {
              return <DefaultCell value={t("accountingsTable.loading")} />;
            }
            
            if (balance > 0) {
              return <DefaultCell value={t("accountingsTable.hasDebt")} />;
            }
            
            return <DefaultCell value={t("accountingsTable.noDebt")} />;
          },
        },
        {
          Header: t("accountingsTable.actions"),
          id: "actions",
          Cell: ({ row }: any) => {
            const accounting: AccountingInterface = row.original;
            return (
              <Button
                id="cy-view-accounting"
                variant="contained"
                color="info"
                onClick={() => openMovementsHandler(accounting)}
              >
                {t("accountingsTable.viewAction")}
              </Button>
            );
          },
        },
      ],
      rows: sortedList,
    };
  };

  return (
    <>
      <DataTable {...props} table={getUITable(modelList)} isLoading={isLoading} 
        onSortChange={props.onSortChange}
        currentSortBy={props.currentSortBy}
        currentSortOrder={props.currentSortOrder}
      />

      <Modal open={activeEditAccountModal} onClose={() => setActiveEditAccountingModal(false)}>
        <AccountsForm
          modelList={modelList}
          setModelList={setModelList}
          action={modalAction}
          model={selectedAccount}
          onCloseAction={() => setActiveEditAccountingModal(false)}
          isModal={true}
        />
      </Modal>
    </>
  );
};

export default AccountingTable;